import { ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { ProjectContextService } from '../../services/project-context.service';
import { UserService } from '../../services/user.service';
import { ProjectSummary } from '../../models/api-dto.model';
import { UserPreferencesService } from '../../services/user-preferences.service';
import { CreateProjectDialogComponent } from '../create-project-dialog/create-project-dialog.component';
import { RenameProjectDialogComponent } from '../rename-project-dialog/rename-project-dialog.component';
import { BrandingDialogComponent } from '../branding-dialog/branding-dialog.component';

@Component({
  selector: 'app-project-selector',
  templateUrl: './project-selector.component.html',
  styleUrls: ['./project-selector.component.scss'],
  standalone: false
})
export class ProjectSelectorComponent implements OnInit, OnDestroy {
  projects: ProjectSummary[] = [];
  isLoading = true;
  activeProjectId: string = '';
  currentUserEmail: string = '';
  private userSub?: Subscription;
  private projectsSub?: Subscription;
  private loadTimeoutId?: any;

  constructor(
    public projectContext: ProjectContextService,
    private userService: UserService,
    private preferencesService: UserPreferencesService,
    private router: Router,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private cdRef: ChangeDetectorRef,
    private ngZone: NgZone
  ) {}

  ngOnInit(): void {
    this.activeProjectId = this.projectContext.getActiveProjectId();

    // 1. Immediately hydrate from cache if already available so user sees content with 0ms delay
    const cached = this.projectContext.userProjects;
    if (cached && cached.length > 0) {
      this.projects = cached;
      this.isLoading = false;
    }

    // 2. Reactively listen to userProjects$ stream
    this.projectsSub = this.projectContext.userProjects$.subscribe(projects => {
      this.ngZone.run(() => {
        if (projects && projects.length > 0) {
          this.projects = projects;
          this.isLoading = false;
        }
        this.cdRef.markForCheck();
        this.cdRef.detectChanges();
      });
    });

    // 3. User email subscription
    this.userSub = this.userService.currentUser$.subscribe(u => {
      this.ngZone.run(() => {
        if (u?.email) {
          this.currentUserEmail = u.email;
        }
        this.cdRef.markForCheck();
        this.cdRef.detectChanges();
      });
    });

    // 4. Trigger fresh fetch
    this.loadProjects();
  }

  ngOnDestroy(): void {
    this.userSub?.unsubscribe();
    this.projectsSub?.unsubscribe();
    if (this.loadTimeoutId) {
      clearTimeout(this.loadTimeoutId);
    }
  }

  loadProjects(): void {
    if (!this.projects || this.projects.length === 0) {
      this.isLoading = true;
      this.cdRef.markForCheck();
    }

    // Safety fallback timer: guarantee isLoading never stays stuck true
    if (this.loadTimeoutId) {
      clearTimeout(this.loadTimeoutId);
    }
    this.loadTimeoutId = setTimeout(() => {
      if (this.isLoading) {
        this.ngZone.run(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
          this.cdRef.detectChanges();
        });
      }
    }, 3500);

    this.projectContext.loadUserProjects(true).subscribe({
      next: (projects) => {
        if (this.loadTimeoutId) {
          clearTimeout(this.loadTimeoutId);
        }
        this.ngZone.run(() => {
          this.projects = projects || [];
          this.isLoading = false;
          this.cdRef.markForCheck();
          this.cdRef.detectChanges();
        });
      },
      error: (err) => {
        if (this.loadTimeoutId) {
          clearTimeout(this.loadTimeoutId);
        }
        console.error('ProjectSelector: error loading projects', err);
        this.ngZone.run(() => {
          this.isLoading = false;
          this.cdRef.markForCheck();
          this.cdRef.detectChanges();
        });
      }
    });
  }

  selectProject(project: ProjectSummary): void {
    this.projectContext.setCurrentProject(project);
    this.router.navigateByUrl(`/p/${project.id}/t/roadmap`);
  }

  openCreateDialog(): void {
    const ref = this.dialog.open(CreateProjectDialogComponent, {
      width: '500px',
      panelClass: 'custom-dialog-container',
      disableClose: false
    });

    ref.afterClosed().subscribe(newProject => {
      if (newProject) {
        this.loadProjects();
      }
    });
  }

  openRenameDialog(project: ProjectSummary, event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.dialog.open(RenameProjectDialogComponent, {
      width: '500px',
      panelClass: 'custom-dialog-container',
      disableClose: false,
      data: { project }
    });

    ref.afterClosed().subscribe(updated => {
      if (updated) {
        this.loadProjects();
      }
    });
  }

  openBrandingDialog(project: ProjectSummary, event: MouseEvent): void {
    event.stopPropagation();
    const ref = this.dialog.open(BrandingDialogComponent, {
      width: '520px',
      panelClass: 'custom-dialog-container',
      disableClose: false,
      data: { project }
    });

    ref.afterClosed().subscribe(updated => {
      if (updated) {
        this.loadProjects();
      }
    });
  }

  getOwnerDisplayEmail(p: ProjectSummary): string {
    return p.ownerEmail || (p.role === 'owner' ? this.currentUserEmail : '') || '';
  }

  getProjectUrl(projectId: string): string {
    const path = this.preferencesService.getLandingPageRoute(projectId);
    return `${window.location.origin}${path}`;
  }

  getProjectDisplayUrl(projectId: string): string {
    return `/p/${projectId}`;
  }

  copyProjectUrl(projectId: string, event: MouseEvent): void {
    event.stopPropagation();
    const url = this.getProjectUrl(projectId);
    navigator.clipboard.writeText(url).then(() => {
      this.snackBar.open('Project URL copied to clipboard', undefined, { duration: 2500 });
    });
  }

  getSupportPortalUrl(projectId: string): string {
    return `${window.location.origin}/support/${projectId}/portal`;
  }

  getSupportPortalDisplayUrl(projectId: string): string {
    return `/support/${projectId}/portal`;
  }

  copySupportPortalUrl(projectId: string, event: MouseEvent): void {
    event.stopPropagation();
    const url = this.getSupportPortalUrl(projectId);
    navigator.clipboard.writeText(url).then(() => {
      this.snackBar.open('Support Portal URL copied to clipboard', undefined, { duration: 2500 });
    });
  }

  confirmDeleteProject(project: ProjectSummary, event: MouseEvent): void {
    event.stopPropagation();
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete "${project.name}"?\n\nNote: Only empty projects (with 0 tasks) can be deleted. This action cannot be undone.`
    );
    if (!confirmed) return;

    this.projectContext.deleteProject(project.id).subscribe({
      next: () => {
        this.snackBar.open(`Project "${project.name}" deleted`, undefined, { duration: 3000 });
        this.loadProjects();
      },
      error: (err) => {
        const message = err?.error?.message || 'Failed to delete project. Please ensure it has no tasks.';
        this.snackBar.open(message, 'Dismiss', { duration: 5000 });
      }
    });
  }

  formatRole(role?: string): string {
    switch (role) {
      case 'owner':
        return 'Owner';
      case 'developer':
        return 'Developer';
      case 'internal_stakeholder':
        return 'Stakeholder (Internal)';
      case 'external_stakeholder':
        return 'Stakeholder (External)';
      default:
        return role ? role.charAt(0).toUpperCase() + role.slice(1) : 'Member';
    }
  }

  roleBadgeClass(role?: string): string {
    switch (role) {
      case 'owner':
        return 'badge-owner';
      case 'developer':
        return 'badge-dev';
      case 'internal_stakeholder':
        return 'badge-stakeholder';
      default:
        return 'badge-default';
    }
  }
}
